// Centralized time formatting helpers.
// Keep both timeFromNow and shortAgo to preserve existing component behavior.
// Small improvement: handle future timestamps gracefully.

/** True if the string already carries a zone designator (Z or ±hh:mm). */
function hasZoneDesignator(value: string): boolean {
  return /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value)
}

/**
 * Parse a server timestamp to epoch millis, always as UTC. The API emits UTC instants,
 * but not always with a zone designator — a value re-read from Cosmos comes back
 * zone-less, and `new Date('…')` then parses it in the VIEWER's local time (hours off in
 * a UTC-behind zone like US Eastern). Pin zone-less strings to UTC. Returns null for
 * empty/invalid input.
 */
export function parseServerTimestamp(value: string | null | undefined): number | null {
  if (!value) return null
  const v = value.trim()
  if (!v) return null
  // Zone already present → parse as-is. A bare date (YYYY-MM-DD) → UTC midnight (matching the
  // old `new Date()` behavior). A zone-less datetime → pin to UTC.
  const normalized = hasZoneDesignator(v)
    ? v
    : /^\d{4}-\d{2}-\d{2}$/.test(v)
      ? `${v}T00:00:00Z`
      : `${v}Z`
  const ms = new Date(normalized).getTime()
  return Number.isNaN(ms) ? null : ms
}

/** parseServerTimestamp as a UTC-pinned Date (or null). Prefer over `new Date(serverValue)`. */
export function parseServerDate(value: string | null | undefined): Date | null {
  const ms = parseServerTimestamp(value)
  return ms === null ? null : new Date(ms)
}

function toDate(input: string | Date): Date {
  return input instanceof Date ? input : new Date(parseServerTimestamp(input) ?? NaN)
}

// Same semantics as existing scattered versions: returns a terse relative string.
// Examples: '3m', '2h', '5d', '3w', '4mo', '2y'
export function timeFromNow(iso: string | Date): string {
  const now = Date.now()
  const d = toDate(iso).getTime()
  if (isNaN(d)) return '—'
  let diff = Math.floor((now - d) / 1000)
  if (diff < 0) diff = 0 // future-proofing
  const minute = 60
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day
  const month = 30 * day
  const year = 365 * day
  if (diff < minute) return `${Math.floor(diff)}s`
  if (diff < hour) return `${Math.floor(diff / minute)}m`
  if (diff < day) return `${Math.floor(diff / hour)}h`
  if (diff < week) return `${Math.floor(diff / day)}d`
  if (diff < month) return `${Math.floor(diff / week)}w`
  if (diff < year) return `${Math.floor(diff / month)}mo`
  return `${Math.floor(diff / year)}y`
}

// Original shortAgo implementation variants: used for interaction lists, similar thresholds.
export function shortAgo(iso?: string | Date | null): string {
  if (!iso) return '—'
  const raw = timeFromNow(iso)
  // Map terse units to suffixed version matching previous panels (e.g. '3m ago')
  if (raw === '0s') return 'just now'
  return raw + ' ago'
}

// Verbose variant preserving earlier phrasing used in leads tables.
// Examples: 'just now', 'a minute ago', '3m ago', 'an hour ago', '5h ago', '2d ago', '3mo ago'
export function timeFromNowVerbose(iso: string | Date): string {
  const d = toDate(iso).getTime()
  if (isNaN(d)) return '—'
  let diff = Date.now() - d
  if (diff < 0) diff = 0
  const sec = Math.floor(diff / 1000)
  if (sec < 45) return 'just now'
  if (sec < 90) return 'a minute ago'
  const min = Math.floor(sec / 60)
  if (min < 45) return `${min}m ago`
  if (min < 90) return 'an hour ago'
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 30) return `${day}d ago`
  const month = Math.floor(day / 30)
  if (month < 12) return `${month}mo ago`
  const year = Math.floor(day / 365)
  return `${year}y ago`
}
